<template>
    <v-container fluid grid-list-md>
        <v-layout wrap>
            <v-flex sm4 xl5>
                <v-layout align-center class="pt-1">
                    <v-subheader>Prosjekter</v-subheader>
                    <v-spacer/>
                    <span class="blue-grey--text d-block pr-3">
                        Sist oppdatert fra Smartdok kl.
                        {{ formatTime(syncTime) }}
                    </span>
                    <v-btn color="green" outlined dark class="mr-1" @click="onSyncClick" :loading="syncing">
                        Synkroniser
                    </v-btn>
                </v-layout>
                <v-card>
                    <v-card-text>
                        <v-text-field label="Fra prosjekt" v-model="filter.project_from" type="number" min="0"
                                      step="1" placeholder="0000000"/>
                        <v-text-field label="Til prosjekt" v-model="filter.project_to" type="number" min="0" step="1"
                                      placeholder="0000000"/>
                    </v-card-text>
                </v-card>

                <v-checkbox class="mt-2 mb-3" label="Begrens periode" :value="!!filter.period_from"
                            @change="togglePeriod"
                            persistent-hint
                            :hint="!!filter.period_from ? 'Med periodebegrensning vil du kun se det som er ført på prosjekter i den valgte perioden.' : 'Viser alt som noen gang er ført på prosjektene i utvalget.'"/>

                <v-radio-group v-model="filter.status" :mandatory="true" row>
                    <v-radio label="Aktive" :value="2"/>
                    <v-radio label="Avsluttet" :value="3"/>
                    <v-radio label="Alle" value="null"/>
                </v-radio-group>


                <v-btn color="primary" class="mx-0 mt-0" @click="applyFilter" :loading="loading">
                    Hent prosjekter
                </v-btn>
            </v-flex>
            <v-flex sm8 xl7>
                <v-layout class="px-1">
                    <v-flex sm6 v-if="filter.period_from">
                        <v-subheader>Fra måned</v-subheader>
                        <v-date-picker v-model="filter.period_from" landscape full-width
                                       type="month" locale="nb"/>
                    </v-flex>
                    <v-flex sm6 v-if="filter.period_to">
                        <v-subheader>Til måned</v-subheader>
                        <v-date-picker v-model="filter.period_to" landscape full-width
                                       type="month" locale="nb"/>
                    </v-flex>
                </v-layout>
            </v-flex>
        </v-layout>
        <v-layout>
            <v-flex v-if="items.length === 0">
                <v-card class="mt-5">
                    <v-card-text class="text-xs-center">
                        <em>Velg prosjektnummer og periode, og trykk "Hent prosjekter" for å lage rapporten.</em>
                    </v-card-text>
                </v-card>
            </v-flex>
            <v-flex v-else>
                <v-layout>
                    <v-subheader>Resultat</v-subheader>
                    <v-spacer/>
                    <v-btn @click="exportExcel" class="green darken-2" dark>
                        <v-icon left>view_list</v-icon>
                        Eksporter til Excel
                    </v-btn>
                </v-layout>
                <v-data-table :headers="headers" :items="items" class="elevation-1 table-condensed"
                              no-data-text="Ingen data funnet" :items-per-page="99999" fixed-header
                              hide-default-footer height="90vh">
                    <template v-slot:body="{ items }">
                        <tr v-for="item in items" :key="item.id">
                            <td class="ledger-status-col">
                                <v-chip small label color="green" dark v-if="item.status === 2">
                                    Aktiv
                                </v-chip>
                                <v-chip small label color="blue-grey lighten-1" dark v-else-if="item.status === 3">
                                    Avsluttet
                                </v-chip>
                            </td>
                            <td><strong>{{ item.id }}</strong></td>
                            <td>{{ item.name }}</td>
                            <!--<td>
                                <DropdownCell :project="item" :items="managerItems" field="manager"/>
                            </td>-->
                            <td class="text-right">
                                <BudgetCell :project="item" field="earnings" :show-diff="showBudgetDiff"
                                            :real-value="projectSums.sum_earnings[item.id]"
                                            label="Anbudssum"/>
                            </td>
                            <td class="text-right">
                                {{ formatMoney(item.sum_earnings) }}
                            </td>
                            <td class="text-right">
                                {{ formatMoney(item.sum_goods) }}
                            </td>
                            <td class="text-right">
                                {{ formatMoney(item.sum_wages) }}
                            </td>
                            <td class="text-right">
                                {{ formatMoney(item.sum_other) }}
                            </td>
                            <td class="text-right">
                                {{ formatMoney(item.sum_equipment) }}
                            </td>
                            <td class="text-right">
                                {{ formatMoney(item.sum_admin) }}
                            </td>
                            <td class="text-right"
                                :class="item.sum_total >= 0 ? 'green--text' : 'red--text'">
                                <strong>{{ formatMoney(item.sum_total) }}</strong>
                            </td>
                            <td class="text-right">
                                <span v-if="item.margin">
                                    {{ formatNumber(item.margin) }} %
                                </span>
                            </td>
                        </tr>
                        <tr v-if="summary" class="font-weight-bold">
                            <td colspan="4">Sum for rapporten</td>
                            <td class="text-right">
                                {{ formatMoney(summary.sum_earnings) }}
                            </td>
                            <td class="text-right">
                                {{ formatMoney(summary.sum_goods) }}
                            </td>
                            <td class="text-right">
                                {{ formatMoney(summary.sum_wages) }}
                            </td>
                            <td class="text-right">
                                {{ formatMoney(summary.sum_other) }}
                            </td>
                            <td class="text-right">
                                {{ formatMoney(summary.sum_equipment) }}
                            </td>
                            <td class="text-right">
                                {{ formatMoney(summary.sum_admin) }}
                            </td>
                            <td class="text-right">
                                {{ formatMoney(summary.sum_total) }}
                            </td>
                            <td class="text-right">
                                <span v-if="summary.avg_margin">
                                    {{ formatNumber(summary.avg_margin) }} %
                                </span>
                            </td>
                        </tr>
                    </template>
                </v-data-table>
            </v-flex>
        </v-layout>
    </v-container>
</template>

<script>
import BudgetCell from './helpers/BudgetCell';
import DropdownCell from './helpers/DropdownCell';
import {formatMoney, formatNumber, formatTime} from '../helpers';

let filterSeries = [
    {from: 3000, to: 3999, text: 'Salgsinntekt', value: 'sum_earnings', align: 'right'},
    {from: 4000, to: 4999, text: 'Varekjøp', value: 'sum_goods', align: 'right'},
    {from: 5000, to: 5999, text: 'Lønnskostnad', value: 'sum_wages', align: 'right'},
    {from: 6000, to: 9999, text: 'Driftskostnader', value: 'sum_other', align: 'right'},
    {from: 3000, to: 9999, text: 'Resultat', value: 'sum_total', align: 'right'},
];

export default {
    components: {
        DropdownCell,
        BudgetCell
    },
    data() {
        return {
            loading: false,
            syncing: false,
            syncTime: null,
            managers: [],
            items: [],
            summary: null,
            filter: {
                project_from: null,
                project_to: null,
                period_from: null,
                period_to: null,
                status: 2,
            },
            projectSums: {},
            adminSums: {},
            equipmentCosts: {},
            showBudgetDiff: false
        }
    },

    created() {
        this.updateStatus();
    },

    methods: {
        togglePeriod() {
            if (this.filter.period_from || this.filter.period_to) {
                this.filter.period_from = null;
                this.filter.period_to = null;
            } else {
                let year = (new Date()).getFullYear();
                this.filter.period_from = `${year}-01`;
                this.filter.period_to = `${year}-12`;
            }
        },
        formatMoney,
        formatNumber,
        formatTime,
        updateStatus() {
            return this.$http.get(`status/`).then(response => {
                this.syncTime = response.data.sync_time;
            });
        },
        loadData() {
            let summary = {
                sum_equipment: 0,
                sum_admin: 0
            };
            filterSeries.forEach(series => {
                summary[series.value] = 0;
            });

            let status = this.filter.status === 'null' ? null : this.filter.status;
            let promises = filterSeries.map(series => {
                let filter = {
                    ...this.filter,
                    account_from: series.from,
                    account_to: series.to,
                    status: status
                };
                return this.$http.get(`summary/`, {params: filter}).then(response => {
                    this.projectSums[series.value] = response.data;
                    summary[series.value] = Object.values(response.data).reduce((sum, a) => sum + a, 0);
                });
            });

            promises.push(this.$http.get(`summary/equipment/`, {params: this.filter}).then(response => {
                this.equipmentCosts = response.data;
            }));
            promises.push(this.$http.get(`project-managers/`, {params: {active: true}}).then(response => {
                this.managers = response.data;
            }));
            promises.push(this.$http.get(`summary/admin/`, {params: this.filter}).then(response => {
                this.adminSums = response.data;
            }));

            return Promise.all(promises).then(() => {
                let projectFilter = {
                    project_from: this.filter.project_from,
                    project_to: this.filter.project_to
                };

                if (this.filter.status !== 'null') {
                    projectFilter['status'] = this.filter.status;
                }

                this.$http.get('projects/', {params: projectFilter}).then(response => {
                    let projects = response.data.filter(project => {
                        return !!Object.values(this.projectSums).find(projectSums => projectSums[project.id]) || typeof this.equipmentCosts[project.id] !== 'undefined' || typeof this.adminSums[project.id] !== 'undefined';
                    });
                    console.log(projects.filter(it => it.id === 2019136))
                    projects.forEach(it => {
                        filterSeries.forEach(series => {
                            it[series.value] = this.projectSums[series.value][it.id];
                        });
                        it.sum_equipment = this.equipmentCosts[it.id] || null;
                        it.sum_admin = this.adminSums[it.id] || null;
                        if(!it.sum_total) it.sum_total = 0;

                        if (it.sum_admin) {
                            summary.sum_admin += it.sum_admin;
                            summary.sum_total += it.sum_admin;
                            it.sum_total += it.sum_admin;
                        }
                        if (it.sum_equipment) {
                            summary.sum_equipment += it.sum_equipment;
                            summary.sum_total += it.sum_equipment;
                            it.sum_total += it.sum_equipment;
                        }

                        it.margin = (it.sum_total / it.sum_earnings) * 100;
                    });
                    this.items = projects;
                    summary.avg_margin = (summary.sum_total / summary.sum_earnings) * 100;
                    this.summary = summary;
                });
            });
        },
        applyFilter() {
            this.loading = true;
            this.loadData().finally(() => {
                this.loading = false;
            })
        },
        exportExcel() {
            this.$http.get('summary/export/', {
                params: this.filter,
                responseType: 'blob'
            }).then(response => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'export.xlsx');
                document.body.appendChild(link);
                link.click();
            })
        },
        onSyncClick() {
            this.syncing = true;
            this.$http.post('sync/').then(response => {
                this.updateStatus();
                this.loadData();
                this.$root.addMessage(request.data.message, 'green');
            }).finally(() => {
                this.syncing = false;
            })
        }
    },

    computed: {
        headers() {
            let filterSeriesWithoutResult = filterSeries.slice(0, filterSeries.length - 1);
            let resultCol = filterSeries[filterSeries.length - 1];
            return [
                {text: 'Status', value: 'status'},
                {text: 'Nr.', value: 'id'},
                {text: 'Navn', value: 'name'},
                //{text: 'Prosjektleder', value: 'manager_id'},
                {text: 'Anbudssum', value: 'budget_earnings', align: 'right'},
                ...filterSeriesWithoutResult,
                {text: 'Maskinkost.', value: 'sum_equipment', align: 'right'},
                {text: 'Adm.kost.', value: 'sum_admin', align: 'right'},
                resultCol,
                {text: 'DG', value: 'margin', align: 'right'}
            ];
        },
        managerItems() {
            return [
                {text: '–', value: null},
                ...this.managers.map(it => {
                    return {
                        text: `${it.first_name} ${it.last_name}`,
                        value: it.id
                    }
                })
            ];
        }
    }
}
</script>

<style>
.ledger-status-col {
    width: 1px;
}

.ledger-status-col .v-chip {
    width: 80px;
    text-align: center;
    justify-content: center;
}
</style>
